/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AssetsService,
  FormHelpersService,
  GoogleAnalyticsService,
  ModalService,
  SmsService,
  SOCIAL_NETWORK,
  ToastrTranslationsNotificationService,
  UtilsService,
} from 'app/shared/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { environment } from 'environments/environment';
import * as fromSharedStore from 'app/shared/store';
import { Constants } from 'app/shared/models/constants.model';

@Component({
  selector: 'app-ig-share-modal',
  templateUrl: './ig-share-modal.component.html',
  styleUrls: ['./ig-share-modal.component.scss'],
})
export class IgShareModalComponent implements OnInit {
  @Input() shareLink?: string;
  @Input() isMobile = false;
  @Input() video?: any;
  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  igShareForm: FormGroup;
  showMobileDownloadNotification: boolean;
  summaryErrorMessage: string;

  private currentUser: any;
  private componentDestroyed: Subject<any> = new Subject();

  constructor(
    private assetsService: AssetsService,
    private gaService: GoogleAnalyticsService,
    private modalService: ModalService,
    private toastrTranslationsNotificationService: ToastrTranslationsNotificationService,
    private utilsService: UtilsService,
    public fhs: FormHelpersService,
    private smsService: SmsService,
    private sharedStore: Store<fromSharedStore.SharedState>
  ) {}

  ngOnInit() {
    this.igShareForm = new FormGroup({
      phoneNumber: new FormControl('', [Validators.required, Validators.pattern(/^[\d]{3}-[\d]{3}-[\d]{4}$/)]),
    });

    this.sharedStore
      .select(fromSharedStore.getUserData)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((currentUser) => {
        this.currentUser = currentUser.userData;
        this.smsService.getPhoneSubscriptionByUserId(this.currentUser.id).subscribe((data) => {
          if (data.length) {
            this.igShareForm.patchValue({ phoneNumber: UtilsService.formatPhoneNumber(data[0].phoneNo.substring(2)) });
          } else if (this.currentUser.userDetails.phoneNumber) {
            this.igShareForm.patchValue({ phoneNumber: this.currentUser.userDetails.phoneNumber });
          }
        });
      });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  close() {
    this.modalService.hideModal();
    this.closeModal.emit(null);
  }

  addPhoneNoSub(phoneNo: string) {
    const action = { sendLink: this.shareLink };
    const userId = this.currentUser.id;
    this.smsService.addPhoneSubscription({ userId, phoneNo, action }).subscribe((data) => {
      if (data.affectedRows) {
        this.sendInitialMessage(phoneNo);
      }
    });
  }

  sendInitialMessage(phoneNo: string) {
    const message = `Reply YES to receive automated NextPro product alerts. Msg & data rates may apply. Reply HELP for help, STOP to cancel.`;
    const to = phoneNo;
    this.smsService.sendMessage({ to, message }).subscribe((response) => {
      if (response.errorMessage) {
        this.summaryErrorMessage = response.errorMessage;
      } else {
        this.close();
      }
    });
  }

  sendLinkMessage(phoneNo: string) {
    const message = `Here is your NextPro clip: ${this.shareLink}. Click the download button and then upload the file to Instagram. Reply HELP for help, STOP to cancel.`;
    const to = phoneNo;
    this.smsService.sendMessage({ to, message }).subscribe((response) => {
      if (response.errorMessage) {
        this.summaryErrorMessage = response.errorMessage;
      } else {
        this.close();
      }
    });
  }

  save() {
    if (this.igShareForm.invalid) {
      return;
    }
    const phoneNo = `+1${this.igShareForm.value.phoneNumber.replace(/\D/g, '')}`;
    this.smsService.getPhoneSubscription(phoneNo).subscribe((phoneSub) => {
      this.gaService.sendEvent('Share', SOCIAL_NETWORK.INSTAGRAM, 'Clipped Video');
      if (!phoneSub || !phoneSub.length) {
        this.addPhoneNoSub(phoneNo);
      } else {
        this.sendLinkMessage(phoneNo);
      }
    });
  }

  download($event, ignoreMobile = false) {
    this.assetsService.downloadAsset(this.video.id).subscribe(() => {});
    this.showMobileDownloadNotification = this.utilsService.isMobileOrTablet() && !ignoreMobile;
    if ((!this.utilsService.isMobileOrTablet() || ignoreMobile) && this.video) {
      this.toastrTranslationsNotificationService.showNotification(
        'VIDEO.CONTAINERS.VIDEO.NOTIFICATION_MESSAGES.DOWNLOADED_DESKTOP'
      );
      let fastlySource =
        this.video.url
          .replace(Constants.GS_ASSET_HOSTNAME + '/video_assets', environment.fastly.main)
          .replace(Constants.VIDEO_QUALITY.q720_30, '')
          .split('.mp4')[0] +
        Constants.VIDEO_QUALITY.q720_30 +
        '.mp4';
      fastlySource = fastlySource.replace(/#/g, '%23');
      window.location.href = fastlySource;
    } else {
      this.toastrTranslationsNotificationService.showNotification(
        'VIDEO.CONTAINERS.VIDEO.NOTIFICATION_MESSAGES.DOWNLOADED_MOBILE'
      );
    }
    this.close();
  }
}
